import { useLazyQuery } from '@apollo/client';
import {
  RegistrationSessionValidationResult,
  ValidateRegistrationSessionInput,
} from '__generated__/globalTypes';
import gql from 'graphql-tag';
import RoutePath from 'App/RoutePath';
import { useHistory } from 'react-router-dom';
import { tracking } from 'App/Tracking';
import getSubdomainFromUrl from 'utils/getSubdomainFromUrl';
import { LoginAction } from 'App/Auth';
import { validateRegistrationSession as ValidateRegistrationSessionResponse } from './__generated__/validateRegistrationSession';

export const VALIDATE_REGISTRATION_SESSION = gql`
  query validateRegistrationSession($input: ValidateRegistrationSessionInput!) {
    validateRegistrationSession(input: $input) {
      result
    }
  }
`;

export enum RegistrationRedirectCode {
  REQUEST_ERROR = 'REQUEST_ERROR', // ApolloError or rate limited
  INVALID_OR_EXPIRED_REGISTRATION_TOKEN = 'INVALID_OR_EXPIRED_REGISTRATION_TOKEN',
}

interface UseValidateRegistrationSessionResult {
  validateRegistrationSession(
    input: ValidateRegistrationSessionInput,
  ): Promise<void>;
  loading: boolean;
}

export const useValidateRegistrationSession =
  (): UseValidateRegistrationSessionResult => {
    const history = useHistory();
    const subdomain = getSubdomainFromUrl();

    const handleError = (code: RegistrationRedirectCode): void => {
      tracking.track('invalid-registration-session', {
        subdomain,
        loginAction: LoginAction.REGISTRATION,
      });
      history.push(RoutePath.Login, {
        registrationRedirectCode: code,
      });
    };

    const handleOnComplete = (
      data: ValidateRegistrationSessionResponse,
    ): void => {
      if (
        data?.validateRegistrationSession?.result !==
        RegistrationSessionValidationResult.VALID
      ) {
        handleError(
          RegistrationRedirectCode.INVALID_OR_EXPIRED_REGISTRATION_TOKEN,
        );
      }
    };

    const [query, { loading }] = useLazyQuery(VALIDATE_REGISTRATION_SESSION, {
      onError: () => handleError(RegistrationRedirectCode.REQUEST_ERROR),
      onCompleted: handleOnComplete,
    });

    const validateRegistrationSession = async ({
      registrationToken,
    }: ValidateRegistrationSessionInput) => {
      await query({ variables: { input: { registrationToken } } });
    };

    return { validateRegistrationSession, loading };
  };
